/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect, useState } from 'react';

import moment from 'moment';

import logger from 'js/app/loggerSingleton';
import Retracked from 'js/app/retracked';
import { FormattedHTMLMessage, FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';
import * as fullStory from 'js/lib/fullstory';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';
import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Grid, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { SvgCheck } from '@coursera/coursera-ui/svg';
import { useTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { getCourseraPlusDisplayNameWithBillingCycle } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import {
  useIsFromPlusMonthlyToAnnualMarketingEmail,
  usePlusMonthlyToAnnualUpgradeData,
} from 'bundles/coursera-plus/utils/plusMonthlyToAnnualUpgradeUtils';
import PaymentsExperiments from 'bundles/epic/clients/payments';
import RazorpayUpdateSubscriptionLink from 'bundles/my-purchases-graphql/components/manage-subscriptions/RazorpayUpdateSubscriptionLink';
import StripeUpdateSubscriptionLink from 'bundles/my-purchases-graphql/components/manage-subscriptions/StripeUpdateSubscriptionLink';
import CancelSubscriptionModalWithData from 'bundles/my-purchases-graphql/components/modals/CancelSubscriptionModalWithData';
import AnnualUpgradeModal from 'bundles/my-purchases-graphql/components/upgrade/AnnualUpgradeModal';
import StartAnnualUpgradeModal from 'bundles/my-purchases-graphql/components/upgrade/StartAnnualUpgradeModal';
import { isNextBillingCycleDiscounted } from 'bundles/my-purchases-graphql/components/utils/isNextBillingCycleDiscounted';
import PageDataContext from 'bundles/my-purchases-graphql/data/PageDataContext';
import type { PageDataContextType } from 'bundles/my-purchases-graphql/data/PageDataContext';
import PageDataProvider from 'bundles/my-purchases-graphql/data/PageDataProvider';
import { getEventingV3Product } from 'bundles/my-purchases-graphql/lib/eventingUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';
import {
  PRODUCT_SKUS_BILLING_CYCLE_MAP,
  isOwnableProductsEnabled,
} from 'bundles/payments-common/hooks/useFindOwnableProducts';
import cartUtils from 'bundles/payments/lib/cartUtils';
import { currencyIdMap } from 'bundles/payments/lib/currencies';
import ReactivateSubscriptionConfirmationModal from 'bundles/subscriptions-common/components/ReactivateSubscriptionConfirmationModal';
import ReactivateSubscriptionModal from 'bundles/subscriptions-common/components/ReactivateSubscriptionModal';
import { useEventTracking, useUndoCancellationData } from 'bundles/subscriptions-common/utils/cancellationUndoUtils';
import BillingCycleType from 'bundles/subscriptions/common/BillingCycleType';
import { createResubscriptionCart, redirectToCheckout } from 'bundles/subscriptions/utils/resubscriptionUtils';

import _t from 'i18n!nls/my-purchases-graphql';

const TrackedButton = Retracked.withVisibilityTracking(withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button));

const FREE_TRIAL_END_OFFSET = 1;

export type ManageSubscriptionCardProps = {
  subscriptionId: string;
  isLiteToPlusUpgrade?: boolean;
  isResubscriptionEligible?: boolean;
};

const useStyles = (theme: Theme) => ({
  rcManageSubscriptionCard: css({
    // padding: '40px',
    marginBottom: '20px',
    position: 'relative',
    backgroundColor: 'var(--cds-color-white-0)',
    margin: 'var(--cds-spacing-300) 0',
    padding: 'var(--cds-spacing-300) var(--cds-spacing-600)',
    border: `1px solid var(--cds-color-white-0)`,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.25s ease-in-out',
    '&:hover': {
      border: '1px solid rgba(0, 0, 0, 0.2)',
    },
    [breakpoints.down('xs')]: {
      padding: 'var(--cds-spacing-300)',
    },
  }),
  updateMethodSpaced: css({
    display: 'inline-block',
    margin: '0 6px',
    verticalAlign: 'text-bottom',
  }),
  pricingPlan: css({
    marginBottom: '24px',
  }),
  nextBillingAt: css({
    marginBottom: '12px',
  }),
  crossedOutOriginalAmount: css({
    textDecoration: 'line-through',
  }),
  discountAmount: css({
    fontWeight: 'bold',
    color: '#1F8354',
  }),
  discountedContainer: css({
    pricingPlan: {
      fontSize: '16px',
    },
    nextBillingAt: {
      fontSize: '14px',
    },
  }),
  cancelLinkWrapper: css({
    p: {
      fontSize: '16px',
      marginBottom: 0,
    },
  }),
  promotionApplied: css({
    fontWeight: 'bold',
    color: '#1F8354',

    svg: {
      marginBottom: '4px',
    },
  }),
  cancelLink: css({
    padding: '0 !important',
    fontWeight: 'normal !important',
    textDecorationLine: 'underline !important',
    fontSize: '16px',
    lineHeight: '24px',
  }),
  renderCTAsWrapper: css({
    marginBottom: '16px',
  }),
  renderCTAs: css({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '32px',
    whiteSpace: 'nowrap',
    [breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '8px',
    },
  }),
  renderCTAsWithSwitchCTA: css({
    alignItems: 'start !important',
  }),
  switchCTAWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
  }),
  onlyShowOnSmAndUp: css({
    display: 'none',
    [breakpoints.up('sm')]: {
      display: 'block',
    },
  }),
  onlyShowOnXsAndDown: css({
    display: 'none',
    [breakpoints.down('xs')]: {
      display: 'block',
    },
  }),
  paddingBottom8: css({
    paddingBottom: 'var(--cds-spacing-100)',
  }),
  alreadySubscribed: css({
    color: '#636363',
    marginTop: '8px',
    [breakpoints.down('xs')]: {
      marginTop: 0,
    },
  }),
  annualUpgradeSavings: css({
    backgroundColor: 'var(--cds-color-grey-25)',
    borderColor: 'var(--cds-color-grey-25)',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0 8px',
    margin: '4px -8px 0',
    borderRadius: '4px',
    color: 'var(--cds-color-grey-975)',
    fontWeight: 700,
    textTransform: 'uppercase',
  }),
});

const displayPriceFromAmount = (amount: number, currencyCode: string) => {
  const digitProps = cartUtils.amountToDigitsProps(amount);
  return <ReactPriceDisplay value={amount} currency={currencyCode} {...digitProps} />;
};

type PropsToComponent = {
  isLiteToPlusUpgrade?: boolean;
  isResubscriptionEligible?: boolean;
};

export const ManageSubscriptionCard = ({ isLiteToPlusUpgrade, isResubscriptionEligible }: PropsToComponent) => {
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
  const [showStartAnnualUpgradeModal, setShowStartAnnualUpgradeModal] = useState(false);
  const [showAnnualUpgradeModal, setShowAnnualUpgradeModal] = useState(false);
  const [showReactivateSubscriptionModal, setShowReactivateSubscriptionModal] = useState(false);
  const [showReactivateSubscriptionConfirmationModal, setShowReactivateSubscriptionConfirmationModal] = useState(false);
  const [createCartLoading, setCreateCartLoading] = useState(false);
  const context = useContext(PageDataContext) as PageDataContextType;
  const track = useTracker();
  const {
    cart,
    subscriptionNextBillDetails,
    s12nProductName,
    productSku,
    ownableProduct,
    mdp,
    s12n,
    query,
    subscription,
  } = context;

  const { isFromPlusMonthlyToAnnualMarketingEmail } = useIsFromPlusMonthlyToAnnualMarketingEmail();
  const { showPlusMonthlyToAnnualUpgrade, courseraPlusAnnualPrice, monthlySavings } = usePlusMonthlyToAnnualUpgradeData(
    subscription,
    'my_purchases'
  );
  const {
    showUndoCancellationCTA,
    monthlySubscriptionPrice,
    getCancelledStatusCopyOnMyPurchases,
    nextBillingDate,
    eligibleSubscriptionIds,
  } = useUndoCancellationData(subscription.productItemId);
  const { reactivateEventingV2Props, trackClickReactivateCta, useGetReactivateCtaTrackingRef } = useEventTracking(
    subscription,
    eligibleSubscriptionIds
  );
  const reactivateCtaTrackingRef = useGetReactivateCtaTrackingRef();
  const { action, s12nId } = query;
  const currentS12nId = s12n?.id;
  const isSubscriptionCancelled = subscription.isCancelled;
  const resubscribeAfterCancellationPeriodEnabled = PaymentsExperiments.preview('resubscribeAfterCancellationPeriod');

  const theme = useTheme();
  const styles = useStyles(theme);

  useEffect(() => {
    if (showPlusMonthlyToAnnualUpgrade && !showStartAnnualUpgradeModal && isFromPlusMonthlyToAnnualMarketingEmail()) {
      setShowStartAnnualUpgradeModal(true);
    }
    // Don't add `showStartAnnualUpgradeModal` to deps or the modal can't get dismissed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPlusMonthlyToAnnualUpgrade]);

  useEffect(() => {
    // `action=cancel&s12nId={s12nId}` is a deep link to trigger the cancel modal on page load
    // Only show the modal if the subscription has not already been cancelled
    if (action === 'cancel' && currentS12nId === s12nId && !isSubscriptionCancelled) {
      setShowCancelSubscriptionModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, currentS12nId, isSubscriptionCancelled]);

  useEffect(() => {
    if (showUndoCancellationCTA) {
      fullStory.start();
      fullStory.set({
        cancellationUndoEnabled_bool: true, // eslint-disable-line camelcase
        surface_str: 'my_purchases', // eslint-disable-line camelcase
      });
    }
  }, [showUndoCancellationCTA]);

  const handleClick = (ev: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    ev.preventDefault();
    setShowCancelSubscriptionModal(true);

    track('click_button', { button: { name: 'cancel_subscription' }, product: getEventingV3Product(subscription) });
  };

  const getBillingCycle = () => {
    if (subscription.isCatalogSubscription) {
      return BillingCycleType.MONTHLY;
    } else if (subscription.isCredentialTrackSubscription) {
      const billingCycleFromProductSku =
        productSku && productSku.numberOfCycles === 1 ? null : BillingCycleType.MONTHLY;
      const billingCycleFromOwnableProduct =
        ownableProduct.numberOfInstallments === 1 ? null : BillingCycleType.MONTHLY;

      return isOwnableProductsEnabled() ? billingCycleFromOwnableProduct : billingCycleFromProductSku;
    } else {
      const billingCycleFromProductSku = productSku && productSku.subscriptionType;
      const billingCycleFromOwnableProduct = ownableProduct?.billingCycle
        ? PRODUCT_SKUS_BILLING_CYCLE_MAP[ownableProduct?.billingCycle]
        : undefined;

      return isOwnableProductsEnabled() ? billingCycleFromOwnableProduct : billingCycleFromProductSku;
    }
  };

  const getBillingMessage = () => {
    const billingCycle = getBillingCycle();

    let billingMessage;
    if (billingCycle === BillingCycleType.MONTHLY) {
      billingMessage = isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails)
        ? _t(
            '{originalPrice} {discountPrice} next {numberOfBillingCycle, plural, =1 {billing cycle} other {{numberOfBillingCycle} billing cycles}}'
          )
        : _t('{price} next billing cycle');
    } else if (billingCycle === BillingCycleType.ANNUAL) {
      billingMessage = _t('{price} per year');
    } else {
      billingMessage = _t('{price}');
    }

    return billingMessage;
  };

  const getSubscriptionPriceMessage = () => {
    const billingMessage = getBillingMessage();
    const billingCycle = getBillingCycle();
    let amount;
    let currencyCode;
    if (subscriptionNextBillDetails) {
      amount = parseFloat(subscriptionNextBillDetails.nextBillingPeriodAmount);
      currencyCode = currencyIdMap[subscriptionNextBillDetails.currencyId].code;
    } else {
      return null;
    }
    const numberOfBillingCycle = subscriptionNextBillDetails?.remainingNumberOfDiscountedBillingCycles ?? 1;

    if (isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails) && billingCycle === BillingCycleType.MONTHLY) {
      const originalAmount = (cart && cart.originalTotalAmount) ?? 0;
      return (
        <FormattedMessage
          message={billingMessage}
          numberOfBillingCycle={numberOfBillingCycle}
          discountPrice={<span css={styles.discountAmount}>{displayPriceFromAmount(amount, currencyCode)}</span>}
          originalPrice={
            <span css={styles.crossedOutOriginalAmount}>{displayPriceFromAmount(originalAmount, currencyCode)}</span>
          }
        />
      );
    } else {
      return <FormattedMessage message={billingMessage} price={displayPriceFromAmount(amount, currencyCode)} />;
    }
  };

  const closeCancelModal = () => {
    setShowCancelSubscriptionModal(false);
  };

  const renderUpdateMethodForRazorpaySubscriptions = () => {
    const { paymentInformation } = subscription;
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const razorpayInfo = paymentInformation?.['org.coursera.payment.RazorpaySubscriptionPaymentInformation'];

    if (!razorpayInfo || !subscriptionNextBillDetails || !subscription.productSku) {
      return null;
    }

    const [productType, productItemId] = stringKeyToTuple(subscription.productSku);

    return (
      <RazorpayUpdateSubscriptionLink
        isSandboxEnabled={razorpayInfo.isSandbox}
        razorpaySubscriptionId={razorpayInfo.razorpaySubscriptionId}
        productType={productType}
        productItemId={s12n?.id ?? productItemId} // use s12nId for s12ns, productSku for Coursera Plus
        amount={subscriptionNextBillDetails.nextBillingPeriodAmount}
        currencyCode={currencyIdMap[subscriptionNextBillDetails.currencyId].code}
      />
    );
  };

  const getNextBillingDateUtcString = (date: number | moment.Moment) => {
    return formatDateTimeDisplay(date, LONG_DATE_ONLY_DISPLAY, 'UTC');
  };

  const renderUpdateMethodForStripeSubscriptions = () => {
    const { stripeSubscriptionPaymentInformation } = subscription;

    if (!stripeSubscriptionPaymentInformation) {
      return null;
    }

    return <StripeUpdateSubscriptionLink />;
  };

  const renderCancelLink = (): React.ReactNode => {
    // MasterTrack should not be cancellable from Manage Subscriptions
    if (!mdp) {
      return (
        <Button
          css={styles.cancelLink}
          size="small"
          component={TrackedA}
          variant="ghost"
          onClick={handleClick}
          data={{ subscriptionId: subscription.id }}
          trackingName="cancel_subscription_button"
          data-testid="cancel-subscription-button"
        >
          {_t('Cancel subscription')}
        </Button>
      );
    }

    return null;
  };

  const handleReactivateSubscriptionClick = () => {
    trackClickReactivateCta();
    setShowReactivateSubscriptionModal(true);
  };

  const handleResubscribeAfterCancellationPeriodClick = async () => {
    setCreateCartLoading(true);
    let productToEnroll;
    const courseraPlusProductIdToEnroll = cart?.getCourseraPlusProductIdToEnroll();
    if (subscription.isCourseraPlusSubscription && courseraPlusProductIdToEnroll) {
      const productItem = stringKeyToTuple(courseraPlusProductIdToEnroll);
      productToEnroll = {
        s12nId: productItem[1],
        courseId: cart?.getCourseraPlusCourseIdToGrantMembership(),
      };
    } else {
      productToEnroll = { courseId: s12n?.courses?.[0]?.id };
    }
    const resubscriptionCart = await createResubscriptionCart(
      subscription,
      productToEnroll.s12nId,
      productToEnroll.courseId
    );
    if (resubscriptionCart?.id) {
      redirectToCheckout(resubscriptionCart.id);
    }
    setCreateCartLoading(false);
  };

  const renderUndoCancelCta = (): React.ReactNode => {
    if (showUndoCancellationCTA) {
      return (
        <TrackedButton
          css={styles.cancelLink}
          size="small"
          component={TrackedA}
          variant="ghost"
          onClick={handleReactivateSubscriptionClick}
          data={reactivateEventingV2Props}
          trackingName="reactivate_subscription_cta"
          data-testid="reactivate-subscription-cta"
        >
          <span ref={reactivateCtaTrackingRef}>{_t('Reactivate subscription')}</span>
        </TrackedButton>
      );
    }

    return null;
  };

  const renderResubscriptionCta = (): React.ReactNode => {
    if (isResubscriptionEligible && resubscribeAfterCancellationPeriodEnabled) {
      return (
        <TrackedButton
          css={styles.cancelLink}
          size="small"
          component={TrackedA}
          variant="ghost"
          disabled={createCartLoading}
          onClick={handleResubscribeAfterCancellationPeriodClick}
          data={reactivateEventingV2Props}
          trackingName="reactivate_after_cancellation_period_cta"
          data-testid="reactivate-after-cancellation-period-cta"
        >
          <span>{_t('Reactivate subscription')}</span>
        </TrackedButton>
      );
    }

    return null;
  };

  const renderSwitchSubscriptionCTA = () => {
    const isAnnualUpgradeEnabled = Boolean(showPlusMonthlyToAnnualUpgrade);
    if (isAnnualUpgradeEnabled) {
      return (
        <>
          <TrackedButton
            size="small"
            variant="ghost"
            css={styles.cancelLink}
            onClick={() => {
              return setShowStartAnnualUpgradeModal(true);
            }}
            trackingData={{ subscriptionId: subscription.id }}
            trackingName="annual_upgrade_subscription_button"
            data-testid="annual-upgrade-button"
          >
            {_t('Switch subscription')}
          </TrackedButton>
        </>
      );
    }
    return null;
  };

  const renderPlusMonthlyToAnnualSubText = () => {
    if (!showPlusMonthlyToAnnualUpgrade || !monthlySavings) {
      return null;
    }

    return (
      <div css={styles.annualUpgradeSavings}>
        <FormattedMessage message="Save {monthlySavings} monthly" monthlySavings={monthlySavings} />
      </div>
    );
  };

  const renderUpdateMethod = () => {
    return (
      <>
        {renderUpdateMethodForRazorpaySubscriptions()}
        {renderUpdateMethodForStripeSubscriptions()}
      </>
    );
  };

  const renderCTAs = () => {
    const swtichSubscriptionCTA = renderSwitchSubscriptionCTA();
    const ctaCss = swtichSubscriptionCTA ? [styles.renderCTAs, styles.renderCTAsWithSwitchCTA] : styles.renderCTAs;

    return (
      <div css={styles.renderCTAsWrapper}>
        <div css={ctaCss}>
          {swtichSubscriptionCTA && (
            <div css={styles.switchCTAWrapper}>
              {swtichSubscriptionCTA}
              {renderPlusMonthlyToAnnualSubText()}
            </div>
          )}
          {renderCancelLink()}
        </div>
      </div>
    );
  };

  const renderPromotionAppliedString = () => {
    return (
      <span css={styles.promotionApplied}>
        <SvgCheck size={24} color="#1F8354" style={{ marginLeft: '8px' }} />
        {_t('Promotion applied.')}
      </span>
    );
  };

  const renderNextBillingChargeMessage = (showPromotionAppliedMessage = false) => {
    const isCredentialTrackBulkPayFromProductSku =
      subscription.isCredentialTrackSubscription && productSku && productSku.numberOfCycles === 1;
    const isCredentialTrackBulkPayFromOwnableProduct =
      subscription.isCredentialTrackSubscription && ownableProduct?.numberOfInstallments === 1;
    const isCredentialTrackBulkPay = isOwnableProductsEnabled()
      ? isCredentialTrackBulkPayFromOwnableProduct
      : isCredentialTrackBulkPayFromProductSku;
    const shouldShowNextChargeMessage = Boolean(!isCredentialTrackBulkPay && subscription.nextBillingAt);

    const pricingMessage = (
      <>
        {getSubscriptionPriceMessage()}
        {/* period to end the "$X next billing cycle" copy */}.
        {showPromotionAppliedMessage && renderPromotionAppliedString()}
      </>
    );
    const nextChargeMessage = (
      <FormattedHTMLMessage
        message={_t('Your card will be charged on <strong>{date}</strong>.')}
        date={getNextBillingDateUtcString(subscription.nextBillingAt)}
      />
    );

    return (
      <Typography2 component="p" css={styles.paddingBottom8} data-testid="pricing-plan" className="pricing-plan">
        {pricingMessage}&nbsp;
        {shouldShowNextChargeMessage ? nextChargeMessage : null}
        <span css={styles.updateMethodSpaced}>{renderUpdateMethod()}</span>
      </Typography2>
    );
  };

  const renderActiveStatus = () => {
    const activeSubscriptionDetails = renderNextBillingChargeMessage(
      isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails)
    );
    if (subscription && subscription.isAppleInApp) {
      return (
        <TrackedDiv
          className="caption-text color-secondary-text"
          trackingName="apple_in_app_message"
          data={{ subscriptionId: subscription.id }}
        >
          <FormattedHTMLMessage
            message={_t(
              `
This purchase was made in the Coursera iOS app.
To request a receipt or cancel your subscription,
<a href="http://support.apple.com/en-us/HT204088" target="_blank">
please view your purchase history in iTunes.</a>
`
            )}
            tagName="p"
          />
        </TrackedDiv>
      );
    } else {
      return (
        <TrackedDiv
          trackingName="active_subscription_card"
          data={{ s12nId: s12n && s12n.id, subscriptionId: subscription?.id }}
          withVisibilityTracking={true}
          data-testid="active-subscription-card"
        >
          {activeSubscriptionDetails}
          {renderCTAs()}
        </TrackedDiv>
      );
    }
  };

  const renderFreeTrialWithNextBillingDiscounted = () => {
    const billingDay = moment(subscription.nextBillingAt);
    const freeTrialEnd = moment(billingDay).subtract(FREE_TRIAL_END_OFFSET, 'days');
    const isMonthlySubscription = getBillingCycle() === BillingCycleType.MONTHLY;

    const freeTrialEndDate = (
      <strong>
        <FormattedMessage
          message={_t('Your free trial ends {freeTrialEnd}')}
          freeTrialEnd={getNextBillingDateUtcString(freeTrialEnd)}
        />
      </strong>
    );

    const subscriptionPriceMessage = getSubscriptionPriceMessage();
    let currencyCode;
    let remainingNumberOfDiscountedBillingCycle;
    if (subscriptionNextBillDetails) {
      currencyCode = currencyIdMap[subscriptionNextBillDetails.currencyId].code;
      remainingNumberOfDiscountedBillingCycle =
        subscriptionNextBillDetails.remainingNumberOfDiscountedBillingCycles ?? 1; // default to 1 month
    }
    const originalAmount = (cart && cart.originalTotalAmount) ?? 0;
    const originalPriceDisplay = currencyCode && displayPriceFromAmount(originalAmount, currencyCode);

    return (
      <TrackedDiv trackingName="free_trial_sub_card" data={{ s12nId: s12n && s12n.id }} withVisibilityTracking={true}>
        {subscription.nextBillingAt && (
          <Typography2 component="p" variant="bodyPrimary" css={styles.paddingBottom8}>
            <FormattedMessage
              message={
                isMonthlySubscription
                  ? _t(
                      "{freeTrialEndDate}. You'll be charged {subscriptionPriceMessage} for {numberOfBillingCycle} {numberOfBillingCycle, plural, =1 {month} other {months}} beginning {billingDay}, then {originalPrice} monthly thereafter (plus any applicable taxes)."
                    )
                  : _t(
                      "{freeTrialEndDate}. You'll be charged {subscriptionPriceMessage} for {numberOfBillingCycle} {numberOfBillingCycle, plural, =1 {year} other {years}} beginning {billingDay}, then {originalPrice} yearly thereafter (plus any applicable taxes)."
                    )
              }
              freeTrialEndDate={freeTrialEndDate}
              subscriptionPriceMessage={subscriptionPriceMessage}
              numberOfBillingCycle={remainingNumberOfDiscountedBillingCycle}
              billingDay={getNextBillingDateUtcString(billingDay)}
              originalPrice={originalPriceDisplay}
            />
            {isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails) && renderPromotionAppliedString()}
            <span css={styles.updateMethodSpaced}>{renderUpdateMethod()}</span>
          </Typography2>
        )}
        {renderCTAs()}
      </TrackedDiv>
    );
  };

  const renderCancelledStatus = () => {
    return (
      <TrackedDiv
        trackingName="cancelled_subscription_card"
        data={{ s12nId: s12n && s12n.id, subscriptionId: subscription?.id }}
        withVisibilityTracking={true}
        data-testid="cancelled-subscription-card"
        css={styles.renderCTAs}
      >
        <div>{showUndoCancellationCTA ? getCancelledStatusCopyOnMyPurchases() : _t('Status: Cancelled')}</div>
        {renderUndoCancelCta()}
      </TrackedDiv>
    );
  };

  const renderInactiveStatus = () => {
    return (
      <TrackedDiv
        trackingName="inactive_subscription_card"
        data={{ s12nId: s12n && s12n.id, subscriptionId: subscription?.id }}
        withVisibilityTracking={true}
        data-testid="inactive-subscription-card"
        css={styles.renderCTAs}
      >
        <div>{_t('Status: Inactive')}</div>
        {renderResubscriptionCta()}
      </TrackedDiv>
    );
  };

  const renderFreeTrial = () => {
    if (isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails)) {
      return renderFreeTrialWithNextBillingDiscounted();
    }

    const billingDay = moment(subscription.nextBillingAt);
    const freeTrialEnd = moment(billingDay).subtract(FREE_TRIAL_END_OFFSET, 'days');

    const freeTrialEndDate = (
      <strong>
        <FormattedMessage
          message={_t('Free trial until {freeTrialEnd}')}
          freeTrialEnd={getNextBillingDateUtcString(freeTrialEnd)}
        />
      </strong>
    );

    const subscriptionPriceMessage = getSubscriptionPriceMessage();

    return (
      <TrackedDiv trackingName="free_trial_sub_card" data={{ s12nId: s12n && s12n.id }} withVisibilityTracking={true}>
        {subscription.nextBillingAt && (
          <Typography2 component="p" variant="bodyPrimary" css={styles.paddingBottom8}>
            <FormattedMessage
              message={_t(
                '{freeTrialEndDate}. You will be charged {subscriptionPriceMessage}, beginning on {billingDay}.'
              )}
              freeTrialEndDate={freeTrialEndDate}
              subscriptionPriceMessage={subscriptionPriceMessage}
              billingDay={getNextBillingDateUtcString(billingDay)}
            />
            {isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails) && renderPromotionAppliedString()}
            <span css={styles.updateMethodSpaced}>{renderUpdateMethod()}</span>
          </Typography2>
        )}
        {renderCTAs()}
      </TrackedDiv>
    );
  };

  const renderSubscriptionInfo = () => {
    // Added the isLiteToPlusUpgrade check because Coursera Plus subscriptions
    // are created to have a free trial but we don't want to show the free trial
    // UI/copy for them. we will just consider them as remaining billing days
    if (subscription.isCancelled) {
      return renderCancelledStatus();
    } else if (subscription.isInactive) {
      return renderInactiveStatus();
    } else if (subscription.isFreeTrial && !isLiteToPlusUpgrade) {
      return renderFreeTrial();
    } else if (subscription.isActive) {
      return renderActiveStatus();
    } else {
      return false;
    }
  };

  const renderProductName = () => {
    if (subscription.isCatalogSubscription) {
      return <FormattedMessage message={_t('Full Catalog Access')} />;
    } else if (s12n) {
      return <FormattedMessage message={s12nProductName} />;
    } else if (mdp) {
      return mdp.name;
    } else if (subscription.isCourseraPlusSubscription) {
      return (
        <FormattedMessage
          message={_t('{courseraPlus} subscription')}
          courseraPlus={getCourseraPlusDisplayNameWithBillingCycle(subscription.productSku || '')}
        />
      );
    } else if (subscription.isCourseraLiteSubscription) {
      return _t('Coursera Lite monthly subscription');
    } else {
      return false;
    }
  };

  let productSlug;
  if (s12n) {
    productSlug = s12n.slug;
  } else if (mdp) {
    productSlug = mdp.slug;
  } else if (subscription.isCourseraPlusSubscription) {
    productSlug = 'coursera-plus-subscription';
  } else if (subscription.isCourseraLiteSubscription) {
    productSlug = 'coursera-lite-subscription';
  } else {
    productSlug = 'catalog-subscription';
  }

  if (
    s12n ||
    mdp ||
    subscription?.isCatalogSubscription ||
    subscription?.isCourseraPlusSubscription ||
    subscription?.isCourseraLiteSubscription
  ) {
    return (
      <div css={styles.rcManageSubscriptionCard}>
        <div id={`subscription-card-${productSlug}`} />
        <Grid container>
          <Grid item xs={12}>
            <Typography
              variant="h2semibold"
              className="subscription-name"
              data-testid="subscription-name"
              css={styles.paddingBottom8}
            >
              {renderProductName()}
            </Typography>
            <Typography2 component="p" color="supportText">
              {renderSubscriptionInfo()}
            </Typography2>
          </Grid>
        </Grid>
        {showCancelSubscriptionModal && (
          <CancelSubscriptionModalWithData onClose={closeCancelModal} subscription={subscription} />
        )}
        <StartAnnualUpgradeModal
          isOpen={showStartAnnualUpgradeModal}
          onClose={() => {
            setShowStartAnnualUpgradeModal(false);
          }}
          openAnnualUpgradeModal={() => {
            setShowStartAnnualUpgradeModal(false);
            setShowAnnualUpgradeModal(true);
          }}
          monthlySavings={monthlySavings}
          subscription={subscription}
          courseraPlusAnnualPrice={courseraPlusAnnualPrice}
        />
        <AnnualUpgradeModal
          subscription={subscription}
          courseraPlusAnnualPrice={courseraPlusAnnualPrice}
          onClose={() => {
            setShowAnnualUpgradeModal(false);
          }}
          onUpgradeSuccess={() => {
            redirect.setLocation('/search');
          }}
          isOpen={showAnnualUpgradeModal}
        />
        <ReactivateSubscriptionModal
          isOpen={showReactivateSubscriptionModal}
          onClose={() => {
            setShowReactivateSubscriptionModal(false);
          }}
          onReactivationSuccess={() => {
            setShowReactivateSubscriptionModal(false);
            setShowReactivateSubscriptionConfirmationModal(true);
          }}
          monthlySubscriptionPrice={monthlySubscriptionPrice}
          subscription={subscription}
          nextBillingDate={nextBillingDate}
          eligibleSubscriptionIds={eligibleSubscriptionIds}
        />

        <ReactivateSubscriptionConfirmationModal
          isOpen={showReactivateSubscriptionConfirmationModal}
          onClose={() => {
            setShowReactivateSubscriptionConfirmationModal(false);
            redirect.refresh();
          }}
          subscription={subscription}
          s12nName={s12n?.name}
        />
      </div>
    );
  } else {
    logger.error(`Unrecognized subscription product type: ${subscription?.productType}`);
    return null;
  }
};

const Wrapper = ({ subscriptionId, isLiteToPlusUpgrade, isResubscriptionEligible }: ManageSubscriptionCardProps) => {
  return (
    <PageDataProvider subscriptionId={subscriptionId}>
      <ManageSubscriptionCard
        isLiteToPlusUpgrade={isLiteToPlusUpgrade}
        isResubscriptionEligible={isResubscriptionEligible}
      />
    </PageDataProvider>
  );
};

export default Wrapper;
